/**
 * Visor del PDF de un consentimiento en las pantallas públicas de firma
 * (enlace del móvil y tablet). Revisión de enlaces 2026-09-07.
 *
 * El <iframe> con el PDF no sirve en el móvil: Safari iOS solo enseña la
 * primera página y no hace scroll; Chrome Android no lo pinta. Por eso:
 *   - siempre hay un botón bien visible «Abrir el documento completo», que
 *     lo abre en el visor nativo (pestaña nueva);
 *   - en pantallas de menos de 768 px se quita el iframe y se explica que lo
 *     abra, lo lea entero y vuelva a firmar;
 *   - en tablet y escritorio se mantiene el iframe debajo del botón.
 */
import React, { useEffect, useState } from 'react';
import { ExternalLink } from 'lucide-react';

const CONSULTA_MOVIL = '(max-width: 767px)';

function usePantallaEstrecha() {
  const consulta = () => typeof window !== 'undefined' && window.matchMedia(CONSULTA_MOVIL).matches;
  const [estrecha, setEstrecha] = useState(consulta);
  useEffect(() => {
    const mq = window.matchMedia(CONSULTA_MOVIL);
    const f = () => setEstrecha(mq.matches);
    // Safari < 14 solo tiene addListener.
    if (mq.addEventListener) mq.addEventListener('change', f); else mq.addListener(f);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', f); else mq.removeListener(f); };
  }, []);
  return estrecha;
}

export default function VisorPdfPublico({ url, alto = 520, testid = 'visor-pdf' }) {
  const movil = usePantallaEstrecha();
  if (!url) {
    return (
      <div className="border border-border rounded-md bg-white flex items-center justify-center text-sm text-muted-foreground" style={{ height: 120 }}>
        Cargando documento…
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid={testid}>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center gap-2 w-full h-12 rounded-md bg-[#2E6E5E] px-4 text-base font-medium text-white hover:bg-[#1E4A3F]"
        data-testid={`${testid}-abrir`}
      >
        <ExternalLink className="w-5 h-5" /> Abrir el documento completo
      </a>
      {movil ? (
        <p className="text-sm text-muted-foreground text-center" data-testid={`${testid}-movil`}>
          Ábralo, léalo entero y vuelva aquí para firmar.
        </p>
      ) : (
        <div className="border border-border rounded-md overflow-hidden bg-white" style={{ height: alto }}>
          <iframe src={url} title="Consentimiento informado" className="w-full h-full" data-testid={`${testid}-iframe`} />
        </div>
      )}
    </div>
  );
}
